import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, Checkbox, Modal, TextArea, TextField, Tooltip, Typography } from '@scale/scaleui';
import { editSession, deleteSession } from '../api';
import { useCreateWebinarSession } from '../hooks/useCreateWebinarSessionStore';
import moment from 'moment';
import { People } from './People';
import { EditAttendance } from './EditAttendance';
import SelectProject from 'react-scale/corp/components/UI/SelectProject';
import { SelectSessionButton } from './SelectSessionButton';
import { formatDuration } from 'react-scale/remotasks.com/components/Expert/Onboarding/WebinarSessionCard';
import _ from 'lodash';
import { ZoomRecordings } from './ZoomRecordings';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { contructGCalLink } from '../helpers';

interface EditSessionsModalProps {
  toggleModal: () => void;
  sessions: any[];
  userId: string;
}

export const EditSessionsModal: React.FC<EditSessionsModalProps> = ({
  toggleModal,
  sessions,
  userId,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { reloadSessions } = useCreateWebinarSession();
  const [applyToFutureSessions, setApplyToFutureSessions] = useState<boolean>(false);
  const futureSessions = useMemo(
    () => sessions.filter(s => moment(s.eventStart).isAfter(moment(Date.now()))),
    [sessions],
  );
  const [currentSession, setCurrentSession] = useState<any>(
    futureSessions.length ? futureSessions[0] : sessions[sessions.length - 1],
  );
  const elapsed = useMemo(
    () =>
      moment(currentSession.eventStart)
        .add(currentSession.duration, 'minutes')
        .isBefore(moment(Date.now())),
    [currentSession.eventStart, currentSession.duration],
  );
  const [name, setName] = useState<string>(currentSession?.name);
  const [description, setDescription] = useState<string>(currentSession?.description);
  const [hostedBy, setHostedBy] = useState<any>(currentSession?.hostedBy);
  const [invitedUsers, setInvitedUsers] = useState<any>(currentSession?.invitedUsers);
  const [invitedWorkerTeams, setInvitedWorkerTeams] = useState<any>(
    currentSession?.invitedWorkerTeams,
  );
  const [invitedQualifications, setInvitedQualifications] = useState<any>(
    currentSession?.invitedQualifications,
  );
  const [invitedSquads, setInvitedSquads] = useState<any>(currentSession?.invitedSquads);
  const [audience, setAudience] = useState<string | null>(currentSession?.audience);
  const [audienceBucket, setAudienceBucket] = useState<number | null>(
    currentSession?.audienceBucket,
  );
  const [rsvpMax, setRsvpMax] = useState<number>(currentSession?.rsvpMax);
  const [minAttendanceDuration, setMinAttendanceDuration] = useState<number>(
    currentSession?.minAttendanceDuration,
  );
  const [projectOption, setProjectOption] = useState<{ label: string; value: string } | null>(null);
  const [attendees, setAttendees] = useState<any>(currentSession?.attendees);
  const attendeesChanged = useMemo(
    () => !_.isEqual(attendees, currentSession?.attendees),
    [attendees, currentSession?.attendees],
  );
  const { editingDisabled, editingDisabledReason } = useMemo(() => {
    if (elapsed && !attendeesChanged) {
      return { editingDisabled: true, editingDisabledReason: 'Session has already ended' };
    }
    if (!currentSession) {
      return { editingDisabled: true, editingDisabledReason: 'No session selected' };
    }
    if (currentSession.canceled) {
      return { editingDisabled: true, editingDisabledReason: 'Session has been canceled' };
    }
    if (isLoading) {
      return { editingDisabled: true, editingDisabledReason: 'Updating...' };
    }
    return { editingDisabled: false, editingDisabledReason: '' };
  }, [elapsed, currentSession, isLoading, attendeesChanged]);

  const handleEditSession = useCallback(() => {
    (async () => {
      setIsLoading(true);
      const changedFields: any = {};
      if (name !== currentSession?.name) changedFields.name = name;
      if (description !== currentSession?.description) changedFields.description = description;
      if (!_.isEqual(hostedBy, currentSession?.hostedBy)) changedFields.hostedBy = hostedBy;
      if (!_.isEqual(invitedUsers, currentSession?.invitedUsers))
        changedFields.invitedUsers = invitedUsers;
      if (!_.isEqual(invitedWorkerTeams, currentSession?.invitedWorkerTeams))
        changedFields.invitedWorkerTeams = invitedWorkerTeams;
      if (!_.isEqual(invitedQualifications, currentSession?.invitedQualifications))
        changedFields.invitedQualifications = invitedQualifications;
      if (!_.isEqual(invitedSquads, currentSession?.invitedSquads))
        changedFields.invitedSquads = invitedSquads;
      if (audience !== currentSession?.audience) changedFields.audience = audience;
      if (audienceBucket !== currentSession?.audienceBucket)
        changedFields.audienceBucket = audienceBucket;
      if (rsvpMax !== currentSession?.rsvpMax) changedFields.rsvpMax = rsvpMax;
      if (minAttendanceDuration !== currentSession?.minAttendanceDuration)
        changedFields.minAttendanceDuration = minAttendanceDuration;
      if (projectOption && projectOption.value !== currentSession?.project)
        changedFields.project = projectOption.value;
      if (!_.isEqual(attendees, currentSession?.attendees)) changedFields.attendees = attendees;
      if (Object.keys(changedFields).length > 0)
        await editSession({ sessionId: currentSession._id, changedFields, applyToFutureSessions });
      setIsLoading(false);
      reloadSessions();
      toggleModal();
    })();
  }, [
    applyToFutureSessions,
    currentSession,
    name,
    description,
    hostedBy,
    invitedQualifications,
    invitedSquads,
    invitedUsers,
    invitedWorkerTeams,
    audience,
    audienceBucket,
    projectOption,
    rsvpMax,
    minAttendanceDuration,
    attendees,
    reloadSessions,
    toggleModal,
  ]);

  useEffect(() => {
    if (currentSession) {
      setName(currentSession.name);
      setDescription(currentSession.description);
      setHostedBy(currentSession.hostedBy);
      setInvitedUsers(currentSession.invitedUsers);
      setInvitedWorkerTeams(currentSession.invitedWorkerTeams);
      setInvitedQualifications(currentSession.invitedQualifications);
      setInvitedSquads(currentSession.invitedSquads);
      setAudience(currentSession.audience);
      setAudienceBucket(currentSession.audienceBucket);
      setRsvpMax(currentSession.rsvpMax);
      setAttendees(currentSession.attendees);
    }
  }, [currentSession]);

  const godMode = useUnleashFeatureFlag('webinar-sessions-god-mode', { userId }, false);

  const handleDeleteSession = useCallback(() => {
    (async () => {
      if (applyToFutureSessions) {
        const sessionsAfterCurrent = sessions.filter(s =>
          moment(s.eventStart).isAfter(currentSession.eventStart),
        );
        await Promise.all(
          [currentSession, ...sessionsAfterCurrent].map(async s => await deleteSession(s._id)),
        );
      } else {
        await deleteSession(currentSession._id);
      }
      reloadSessions();
      toggleModal();
    })();
  }, [applyToFutureSessions, reloadSessions, toggleModal, sessions, currentSession]);

  return (
    <Modal
      title="Edit Session(s)"
      open={true}
      onClose={toggleModal}
      actions={
        <div className="flex items-center gap-2 justify-end">
          {!elapsed && (
            <Checkbox
              className="ml-auto"
              label="Apply to future sessions"
              checked={applyToFutureSessions}
              onChange={e => setApplyToFutureSessions(e.target.checked)}
            />
          )}
          {godMode && (
            <Button onClick={handleDeleteSession} variant="danger">
              Delete Session(s)
            </Button>
          )}
          <Tooltip
            delayDuration={0}
            side="left"
            text="Only adds this singular session to your Google Calendar"
            target={
              <a href={contructGCalLink(currentSession)} target="_blank" rel="noopener noreferrer">
                <Button variant="secondary">Add to Google Calendar</Button>
              </a>
            }
          />
          <Tooltip
            delayDuration={0}
            side="left"
            text={editingDisabledReason}
            disabled={!editingDisabled}
            target={
              <Button disabled={editingDisabled} onClick={handleEditSession}>
                {isLoading ? 'Updating...' : 'Update Session(s)'}
              </Button>
            }
          />
        </div>
      }
    >
      <div className="scaleui scale-tailwind flex h-full">
        <div className="h-full border-r border-neutral-200">
          <div className="flex flex-col">
            {sessions.map((s: any) => (
              <SelectSessionButton
                key={s._id}
                onClick={() => setCurrentSession(s)}
                selected={currentSession?._id === s?._id}
                title={`${moment(s.eventStart).format('dddd MMM D, YYYY [at] h[:]mmA')}${
                  s.canceled ? ' (Canceled)' : ''
                }`}
                elapsed={moment(s.eventStart).isBefore(moment(Date.now()))}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 px-2">
          <div className="flex items-end">
            <Typography.H4 className="mr-4">
              {moment(currentSession?.eventStart).format('dddd MMM D, YYYY [at] h[:]mmA')}{' '}
              {currentSession?.canceled && '(Canceled)'}
            </Typography.H4>
            <Typography.Paragraph2 className="ml-auto">
              Duration: {formatDuration(currentSession?.duration)}
            </Typography.Paragraph2>
          </div>
          <TextField
            label="Name"
            placeholder="Enter session name"
            value={name ?? ''}
            onChange={e => setName(e.target.value)}
            disabled={editingDisabled}
          />
          <div className="flex gap-1">
            <Typography.Subtitle3>ID:</Typography.Subtitle3>
            <Typography.Paragraph2>{currentSession._id}</Typography.Paragraph2>
          </div>
          {elapsed && <ZoomRecordings session={currentSession} />}
          <People
            invitedUsers={invitedUsers}
            setInvitedUsers={setInvitedUsers}
            audience={audience}
            setAudience={setAudience}
            audienceBucket={audienceBucket}
            setAudienceBucket={setAudienceBucket}
            invitedWorkerTeams={invitedWorkerTeams}
            setInvitedWorkerTeams={setInvitedWorkerTeams}
            invitedQualifications={invitedQualifications}
            setInvitedQualifications={setInvitedQualifications}
            invitedSquads={invitedSquads}
            setInvitedSquads={setInvitedSquads}
            hostedBy={hostedBy}
            setHostedBy={setHostedBy}
            projectId={currentSession?.project}
            disabled={editingDisabled}
          />
          <TextField
            label="Max RSVPs (optional)"
            type="number"
            className="w-[180px]"
            value={rsvpMax ?? ''}
            onChange={e => setRsvpMax(Number(e.target.value))}
            min={1}
            step={1}
            disabled={editingDisabled}
          />
          <TextField
            label="Min Attendance Duration in Minutes (optional)"
            type="number"
            className="w-[360px]"
            value={minAttendanceDuration ?? ''}
            onChange={e => setMinAttendanceDuration(Number(e.target.value))}
            min={0}
            max={currentSession?.duration ?? 0}
            step={1}
            disabled={editingDisabled}
            error={(minAttendanceDuration ?? 0) > (currentSession?.duration ?? 0)}
          />
          <Typography.Subtitle3 className="text-neutral-600">Project</Typography.Subtitle3>
          {currentSession?.project || elapsed ? (
            <Typography.Paragraph3>{currentSession.project}</Typography.Paragraph3>
          ) : (
            <SelectProject
              onChange={(projectOption: { label: string; value: string } | null) => {
                setProjectOption(projectOption);
              }}
              value={projectOption}
            />
          )}
          <TextArea
            label="Description (optional)"
            placeholder="Enter session description"
            value={description ?? ''}
            onChange={e => setDescription(e.target.value)}
            disabled={editingDisabled}
          />
          {elapsed && (
            <EditAttendance
              session={currentSession}
              attendees={attendees}
              setAttendees={setAttendees}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
